import { Injectable } from '@angular/core';
import {
  CadSteps,
  DutyDrawbackSteps,
  MassAdjustmentSteps,
  SDASteps,
  TempImpSteps,
  ConvertB3Steps,
  MPRCCSteps,
  RDDBSteps,
} from '../models/cad-steps.model';
import { CadStep, CADType, DeclarationType, IStep, IStepState } from '../models/common';
import { ICanUseStepper } from '../models/form';

@Injectable()
export class StepperService {
  private _canRoute = true;

  get canRoute() {
    return this._canRoute;
  }

  set canRoute(value: boolean) {
    this._canRoute = value;
  }

  public getSteps(cadType: CADType): IStep[] {
    switch (cadType) {
      case CADType.DECLARATION:
        return CadSteps;
      case CADType.TEMPORARY_IMPORTATION:
        return TempImpSteps;
      case CADType.DUTY_DRAWBACK:
        return DutyDrawbackSteps;
      case CADType.CONVERT_B3:
        return ConvertB3Steps;
      case CADType.MASS_ADJUSTMENT:
        return MassAdjustmentSteps;
      case CADType.SDA:
        return SDASteps;
      case CADType.MPRCC:
        return MPRCCSteps;
      case CADType.RDDB:
        return RDDBSteps;
      default:
        console.error(cadType.toString() + ' not supported');
        return [];
    }
  }

  public getDeclarationStepsByType(declarationType: string): IStep[] {
    switch (declarationType) {
      case DeclarationType.WarehouseIn_10:
      case DeclarationType.ReWarehouse_13:
      case DeclarationType.ExWarehouseForConsumption_20:
      case DeclarationType.ExWarehouseForExport_21:
      case DeclarationType.TransferOfGoods_30: {
        return CadSteps;
      }
      case DeclarationType.WithRelease_AB:
      case DeclarationType.ToObtainReleaseOfGoods_C:
      case DeclarationType.CourierLowValueShipment_F:
      case DeclarationType.CustomsSelfAssessment_TT:
      case DeclarationType.Voluntary_V: {
        return CadSteps.filter(step => step.id !== CadStep.Warehouse);
      }
      default: {
        return CadSteps;
      }
    }
  }

  public getConvertB3StepsByType(declarationType: string): IStep[] {
    switch (declarationType) {
      case DeclarationType.WarehouseIn_10:
      case DeclarationType.ReWarehouse_13:
      case DeclarationType.ExWarehouseForConsumption_20:
      case DeclarationType.ExWarehouseForExport_21:
      case DeclarationType.TransferOfGoods_30: {
        return ConvertB3Steps;
      }
      case DeclarationType.WithRelease_AB:
      case DeclarationType.ToObtainReleaseOfGoods_C:
      case DeclarationType.CourierLowValueShipment_F:
      case DeclarationType.CustomsSelfAssessment_TT:
      case DeclarationType.Voluntary_V: {
        return ConvertB3Steps.filter(step => step.id !== CadStep.Warehouse);
      }
      default: {
        return ConvertB3Steps;
      }
    }
  }

  public getNextStep(cadType: CADType, currentStep: string): IStep {
    const allSteps = this.getSteps(cadType);

    const currentStepStateIndex = allSteps.findIndex(s => s.id === currentStep);

    if (allSteps.length > currentStepStateIndex) {
      const nextStepState = allSteps[currentStepStateIndex + 1];
      return allSteps.find(s => s.id === nextStepState.id);
    }
    return null;
  }

  public getPreviousStep(cadType: CADType, currentStep: string): IStep {
    const allSteps = this.getSteps(cadType);

    const currentStepStateIndex = allSteps.findIndex(s => s.id === currentStep);

    if (allSteps.length > currentStepStateIndex && currentStepStateIndex > 0) {
      const previousStepState = allSteps[currentStepStateIndex - 1];
      return allSteps.find(s => s.id === previousStepState.id);
    }
    return null;
  }

  public getCurrentStep(formModel: ICanUseStepper): IStep {
    const currentStep = this.getSteps(formModel.cadType).find(
      s => s.id === formModel.currentStep
    );
    return currentStep;
  }

  public getStepById(formModel: ICanUseStepper, stepId: string): IStep {
    const currentStep = this.getSteps(formModel.cadType).find(
      s => s.id === stepId
    );
    return currentStep;
  }

  public getCurrentStepState(formModel: ICanUseStepper): IStepState {
    const currentStepState = formModel.steps.find(
      s => s.id === formModel.currentStep
    );
    return currentStepState;
  }

  public getStepStateById(
    formModel: ICanUseStepper,
    stepId: string
  ): IStepState {
    const currentStepState = formModel.steps.find(s => s.id === stepId);
    return currentStepState;
  }
}
